import React, { useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { Dialog, DialogContent, IconButton, List, ListItem, ListItemText } from '@mui/material';
import PropTypes from 'prop-types';
import moment from 'moment';
import ReactPlayer from 'react-player';
import { useSnackbar } from 'notistack';
import palette from '../theme/palette';
import MenuPopover from './MenuPopover';
import PageIcon from '../svg/PageIcon';
import YoutubeIcon from '../svg/YoutubeIcon';
import LinkIcon from '../svg/LinkIcon';

const useStyles = makeStyles({
  container: {
    marginTop: 10,
    display: 'flex',
    padding: 10,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    backgroundColor: 'white',
    borderRadius: 8,
    alignItems: 'center',
    cursor: 'pointer'
  },
  rightIcon: {
    marginLeft: 'auto',
    marginRight: 10
  },
  leftIconContainer: {
    backgroundColor: palette.secondary.lighter,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 6,
    marginRight: 15,
    width: 60,
    height: 60
  },
  titleStyle: {
    fontSize: 16
  },
  subTitleStyle: {
    fontSize: 13,
    color: palette.grey[500]
  },
  modalStyle: {
    maxWidth: 'unset'
    // zIndex: 10
  }
});

export default function FileContainer({
  id,
  title,
  file,
  type,
  date,
  rightIcon,
  onDeletePress,
  onDownloadPress,
  onMoveCopyPress,
  onSharePress,
  onRenamePress
}) {
  const classes = useStyles();
  const anchorRef = useRef();
  const [open, setOpen] = useState(false);
  const fileRef = useRef();
  const [showPlayer, setShowPlayer] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };
  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  };

  const copyToClipboard = () => {
    if ('clipboard' in navigator) {
      return navigator.clipboard.writeText(file);
    }
    fileRef.current.select();
    document.execCommand('copy');
    return null;
  };

  const downloadFile = () => {
    fetch(file)
      .then((r) => r.blob())
      .then((blob) => {
        const element = document.createElement('a');
        document.body.appendChild(element);
        element.setAttribute('href', window.URL.createObjectURL(blob));
        element.setAttribute('download', title);
        element.style.display = '';

        element.click();

        document.body.removeChild(element);
      })
      .catch(() =>
        enqueueSnackbar('There is something wrong while downloading', {
          variant: 'error'
        })
      );
  };

  const openFile = () => {
    if (type === 'file' || type === 'link') window.open(file, '_blank');
    if (type === 'youtube') setShowPlayer(true);
  };

  return (
    <div className={classes.container} key={id} role="presentation" onClick={() => openFile()}>
      <div className={classes.leftIconContainer}>
        {type === 'file' && <PageIcon color={palette.secondary.main} />}
        {type === 'youtube' && <YoutubeIcon color={palette.secondary.main} />}
        {type === 'link' && <LinkIcon color={palette.secondary.main} />}
      </div>
      <div>
        <p style={{ display: 'none' }} ref={fileRef}>
          {file}
        </p>
        <p className={classes.titleStyle}>{title}</p>
        <p className={classes.subTitleStyle}>{moment(date).format('ddd, DD MMM, LT')}</p>
      </div>
      <div className={classes.rightIcon}>
        <IconButton onClick={handleOpen} ref={anchorRef}>
          {rightIcon}
        </IconButton>
        <MenuPopover
          open={open}
          onClose={handleClose}
          anchorEl={anchorRef.current}
          sx={{ width: 220 }}
        >
          <List>
            {onRenamePress && type !== 'file' && (
              <ListItem
                button
                divider
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                  onRenamePress();
                }}
              >
                <ListItemText primary="Edit" />
              </ListItem>
            )}
            {onDownloadPress && type === 'file' && (
              <ListItem
                button
                divider
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                  onDownloadPress();
                  downloadFile();
                }}
              >
                <ListItemText primary="Download" />
              </ListItem>
            )}
            {(type === 'youtube' || type === 'link') && (
              <ListItem
                button
                divider
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                  if (type === 'youtube') {
                    setShowPlayer(true);
                  }
                  if (type === 'link') {
                    window.open(file, { target: '_blank' });
                  }
                }}
              >
                <ListItemText primary={type === 'youtube' ? 'Watch video' : 'Open link'} />
              </ListItem>
            )}
            {onDeletePress && (
              <ListItem
                button
                divider
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                  onDeletePress();
                }}
              >
                <ListItemText primary="Delete" />
              </ListItem>
            )}
            {onMoveCopyPress && type !== 'file' && (
              <ListItem
                button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                  onMoveCopyPress();
                  copyToClipboard();
                }}
              >
                <ListItemText primary="Copy Link" />
              </ListItem>
            )}
            {onSharePress && (
              <ListItem
                button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClose(e);
                  onSharePress();
                }}
              >
                <ListItemText primary="Share" />
              </ListItem>
            )}
          </List>
        </MenuPopover>
      </div>
      <Dialog
        open={showPlayer}
        onClose={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowPlayer(false);
        }}
        classes={{ paper: classes.modalStyle }}
      >
        <DialogContent>
          <ReactPlayer url={file} controls />
        </DialogContent>
      </Dialog>
    </div>
  );
}

FileContainer.propTypes = {
  title: PropTypes.string,
  id: PropTypes.string,
  date: PropTypes.string,
  file: PropTypes.string,
  type: PropTypes.string,
  rightIcon: PropTypes.any,
  onDeletePress: PropTypes.func,
  onDownloadPress: PropTypes.func,
  onMoveCopyPress: PropTypes.func,
  onSharePress: PropTypes.func,
  onRenamePress: PropTypes.func
};
